import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dumbbell, BarChart3, Plus, Settings, Calendar, Activity, DollarSign, Layers,
  Users, Video, Clock, Check, X, Trash2, CheckCircle, Tag, Home, Search,
  ChevronRight, Bell, Trophy, Flame, Target, TrendingUp, Zap, ChevronLeft, Presentation, ClipboardList
} from "lucide-react";
import { toast } from "sonner";
import EquipmentManager from "./coach/EquipmentManager";
import ProductsManager from "./coach/ProductsManager";
import ExerciseLibrary from "./coach/ExerciseLibrary";
import WorkoutLibrary from "./coach/WorkoutLibrary";
import ProgramLibrary from "./coach/ProgramLibrary";
import TierGoalTemplatesManager from "./coach/TierGoalTemplatesManager";
import CouponManager from "./CouponManager";
import NutritionBuilder from "./coach/NutritionBuilder";
import CoachSchedule from "./coach/CoachSchedule";
import PresentationsManager from "./coach/PresentationsManager";
import TrashManager from "./coach/TrashManager";
import SubscribersManager from "./coach/SubscribersManager";
import AssessmentsManager from "./coach/AssessmentsManager";
import VideoLibrary from "./coach/VideoLibrary";
import { AtRiskAthletesPanel, CoachNoteModal } from "./AtRiskAthletesPanel";
import { WinsOfTheWeekPanel } from "./WinsOfTheWeekPanel";
import { API } from "./coach/SharedComponents";

// ==================== COLOR PALETTE ====================
// Primary: Blue (#3B82F6) - matches logo
// Background: Dark Navy (#0B0F1A)
// Surface: Slightly lighter (#141B2D)
// Border: (#1E293B)
// Accent: Emerald for success, Amber for warnings, Purple for special

// ==================== DASHBOARD VIEW TOGGLE ====================
export const DashboardViewToggle = ({ currentView, onViewChange }) => {
  const views = [
    { id: "admin", label: "Admin", icon: Settings, activeColor: "from-red-500 to-rose-600" },
    { id: "coach", label: "Coach", icon: Dumbbell, activeColor: "from-blue-500 to-blue-600" },
    { id: "athlete", label: "Athlete", icon: Activity, activeColor: "from-emerald-500 to-green-600" },
  ];

  return (
    <div className="flex bg-[#141B2D] border border-[#1E293B] rounded-full p-1" data-testid="dashboard-view-toggle">
      {views.map((view) => (
        <button
          key={view.id}
          onClick={() => onViewChange(view.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            currentView === view.id
              ? `bg-gradient-to-r ${view.activeColor} text-white shadow-lg`
              : "text-gray-400 hover:text-white"
          }`}
          data-testid={`view-toggle-${view.id}`}
        >
          <view.icon className="w-4 h-4" />
          <span className="font-medium text-sm">{view.label}</span>
        </button>
      ))}
    </div>
  );
};

// ==================== ACTIVITY RING COMPONENT ====================
const ActivityRing = ({ progress, color, size = 56, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  const colorMap = {
    blue: '#3B82F6',
    green: '#22C55E',
    purple: '#A855F7',
    orange: '#F97316',
    amber: '#F59E0B',
    emerald: '#10B981',
    cyan: '#06B6D4'
  };
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#1E293B"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={colorMap[color] || colorMap.blue}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  );
};

// ==================== COACH DASHBOARD (Dark Theme) ====================
export const CoachDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    // Safety timeout: if request hangs longer than 15s, abort and stop spinner
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${API}/coach/stats`, { credentials: "include", signal: controller.signal });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        console.error("[CoachDashboard] /coach/stats returned", response.status);
      }
    } catch (error) {
      console.error("[CoachDashboard] Failed to fetch stats:", error);
      if (error.name === "AbortError") {
        toast.error("Coach stats took too long to load. Please refresh.");
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const menuItems = [
    { id: "overview", label: "Overview", icon: Home, color: "blue" },
    { id: "exercises", label: "Exercises", icon: Dumbbell, count: stats?.exercises_count || 0, color: "blue" },
    { id: "workouts", label: "Workouts", icon: Layers, count: stats?.workouts_count || 0, color: "emerald" },
    { id: "programs", label: "Programs", icon: Calendar, count: stats?.programs_count || 0, color: "purple" },
    { id: "coaching", label: "Athletes", icon: Users, color: "amber" },
    { id: "tier_goals", label: "Tier Goals", icon: Target, color: "amber" },
    { id: "coupons", label: "Coupons", icon: Tag, color: "cyan" },
    { id: "equipment", label: "Equipment", icon: Settings, color: "gray" },
    { id: "nutrition", label: "Nutrition", icon: Activity, color: "lime" },
    { id: "schedule", label: "Schedule", icon: Calendar, color: "orange" },
    { id: "products", label: "Products", icon: DollarSign, color: "green" },
    { id: "presentations", label: "Presentations", icon: Presentation, color: "indigo" },
    { id: "assessments", label: "Assessments", icon: ClipboardList, color: "teal" },
    { id: "videos", label: "Videos", icon: Video, color: "rose" },
    { id: "trash", label: "Trash", icon: Trash2, color: "red" },
  ];

  const featuredCards = [
    { 
      id: "exercises",
      title: "Exercise Library", 
      subtitle: `${stats?.exercises_count || 0} exercises`, 
      icon: Dumbbell,
      gradient: "from-blue-600 to-blue-500",
      shadowColor: "shadow-blue-500/20",
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&q=80"
    },
    { 
      id: "workouts",
      title: "Workout Templates", 
      subtitle: `${stats?.workouts_count || 0} workouts`, 
      icon: Layers,
      gradient: "from-emerald-600 to-emerald-500",
      shadowColor: "shadow-emerald-500/20",
      image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&q=80"
    },
    { 
      id: "programs",
      title: "Programs", 
      subtitle: `${stats?.programs_count || 0} programs`, 
      icon: Calendar,
      gradient: "from-purple-600 to-purple-500",
      shadowColor: "shadow-purple-500/20",
      image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&q=80"
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-[#0B0F1A] min-h-[60vh] rounded-2xl">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-80px)] bg-[#0B0F1A] -mx-4 md:-mx-8 -mt-6 rounded-t-3xl overflow-hidden">
      {/* Sidebar */}
      <div className={`bg-[#0F1419] border-r border-[#1E293B] flex flex-col transition-all duration-300 ${
        sidebarCollapsed ? "w-20" : "w-64"
      } hidden md:flex`}>
        {/* Logo */}
        <div className="p-5 border-b border-[#1E293B]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 flex-shrink-0">
              <span className="text-white font-black text-lg">P</span>
            </div>
            {!sidebarCollapsed && (
              <div>
                <span className="font-bold text-white text-sm tracking-wide">PALADIN</span>
                <span className="block text-[9px] text-gray-500 tracking-wider">COACH PORTAL</span>
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="flex items-center gap-2 px-4 py-3 bg-[#141B2D] border border-[#1E293B] rounded-xl text-gray-500 hover:border-blue-500/50 transition-colors cursor-pointer">
              <Search className="w-4 h-4" />
              <span className="text-sm">Search...</span>
              <kbd className="ml-auto text-[10px] bg-[#1E293B] px-1.5 py-0.5 rounded text-gray-600">⌘K</kbd>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                activeTab === item.id
                  ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-400 hover:text-white hover:bg-[#141B2D]"
              }`}
              data-testid={`coach-tab-${item.id}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <>
                  <span className="font-medium flex-1 text-left text-sm">{item.label}</span>
                  {item.count !== undefined && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                      activeTab === item.id ? "bg-white/20" : "bg-[#1E293B] text-gray-500"
                    }`}>
                      {item.count}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>

        {/* Collapse Toggle */}
        <button 
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-4 border-t border-[#1E293B] text-gray-600 hover:text-white text-xs flex items-center justify-center gap-2 transition-colors"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${sidebarCollapsed ? "rotate-180" : ""}`} />
          {!sidebarCollapsed && <span>Collapse</span>}
        </button>
      </div>

      {/* Mobile Tab Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0F1419]/95 backdrop-blur-xl border-t border-[#1E293B] z-40 px-2 py-2">
        <div className="flex justify-around">
          {menuItems.slice(0, 5).map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${
                activeTab === item.id ? "text-blue-400" : "text-gray-500"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto pb-20 md:pb-0 bg-[#0B0F1A]">
        {/* Header */}
        <header className="sticky top-0 bg-[#0B0F1A]/90 backdrop-blur-xl z-10 px-6 md:px-8 py-6 border-b border-[#1E293B]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Coach Dashboard</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white capitalize">
                {activeTab === "overview" ? "Overview" : menuItems.find(m => m.id === activeTab)?.label}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button className="p-3 bg-[#141B2D] border border-[#1E293B] rounded-xl hover:border-blue-500/50 transition-colors hidden md:flex">
                <Bell className="w-5 h-5 text-gray-400" />
              </button>
              {activeTab !== "overview" && (
                <button 
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 md:px-5 py-2.5 md:py-3 rounded-xl font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all text-sm"
                >
                  <Plus className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="hidden sm:inline">Create New</span>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6 md:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === "overview" && (
                <CoachOverviewDark stats={stats} featuredCards={featuredCards} onNavigate={setActiveTab} />
              )}
              {activeTab === "exercises" && <ExerciseLibrary />}
              {activeTab === "workouts" && <WorkoutLibrary />}
              {activeTab === "programs" && <ProgramLibrary />}
              {activeTab === "coaching" && <CoachingManagerDark />}
              {activeTab === "tier_goals" && <TierGoalTemplatesManager />}
              {activeTab === "coupons" && <CouponManager />}
              {activeTab === "equipment" && <EquipmentManager />}
              {activeTab === "nutrition" && <NutritionBuilder />}
              {activeTab === "schedule" && <CoachSchedule />}
              {activeTab === "products" && <ProductsManager />}
              {activeTab === "presentations" && <PresentationsManager />}
              {activeTab === "assessments" && <AssessmentsManager />}
              {activeTab === "videos" && <VideoLibrary />}
              {activeTab === "trash" && <TrashManager />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

// ==================== COACH OVERVIEW (Dark Theme) ====================
const CoachOverviewDark = ({ stats, featuredCards, onNavigate }) => {
  const [noteModalAthlete, setNoteModalAthlete] = useState(null);

  const quickStats = [
    { label: "Active Athletes", value: stats?.users || 0, icon: Users, gradient: "from-blue-600 to-blue-500", shadow: "shadow-blue-500/20" },
    { label: "Workouts Today", value: "12", icon: Flame, gradient: "from-orange-600 to-amber-500", shadow: "shadow-orange-500/20" },
    { label: "Weekly Goal", value: "78%", icon: Target, gradient: "from-emerald-600 to-green-500", shadow: "shadow-emerald-500/20" },
    { label: "Revenue", value: `$${((stats?.revenue || 0) / 100).toFixed(0)}`, icon: TrendingUp, gradient: "from-purple-600 to-violet-500", shadow: "shadow-purple-500/20" },
  ];

  return (
    <div className="space-y-8">
      {/* Activity Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickStats.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className={`bg-[#141B2D] border border-[#1E293B] rounded-2xl p-5 md:p-6 hover:border-blue-500/30 transition-all ${stat.shadow}`}
          >
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl mb-3 md:mb-4 flex items-center justify-center bg-gradient-to-br ${stat.gradient} shadow-lg ${stat.shadow}`}>
              <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</p>
            <p className="text-xs md:text-sm text-gray-500">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Featured Cards */}
      <div>
        <h2 className="text-lg md:text-xl font-bold text-white mb-4">Your Content</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {featuredCards.map((card, i) => (
            <motion.button
              key={i}
              onClick={() => onNavigate(card.id)}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className={`relative group cursor-pointer overflow-hidden rounded-2xl md:rounded-3xl aspect-[4/3] text-left shadow-xl ${card.shadowColor}`}
            >
              <img 
                src={card.image} 
                alt={card.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${card.gradient} opacity-90`}></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end text-white">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-xl mb-3 md:mb-4 flex items-center justify-center">
                  <card.icon className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-1">{card.title}</h3>
                <p className="text-white/80 text-sm md:text-base">{card.subtitle}</p>
              </div>
              <div className="absolute top-4 right-4 w-8 h-8 md:w-10 md:h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* At-Risk Athletes — rescue outreach */}
        <AtRiskAthletesPanel />

        {/* Wins of the Week — celebration outreach */}
        <WinsOfTheWeekPanel onLeaveNote={(a) => setNoteModalAthlete(a)} />

        {/* Weekly Summary */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl md:rounded-3xl p-5 md:p-6 text-white shadow-xl shadow-blue-500/25">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold">Weekly Summary</h2>
              <p className="text-blue-100 text-xs md:text-sm">Great progress!</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {[
              { label: "Workouts", value: "47" },
              { label: "Hours", value: "32" },
              { label: "Calories", value: "18.4k" },
              { label: "PRs Hit", value: "8" },
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 hover:bg-white/20 transition-colors">
                <p className="text-2xl md:text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-blue-100 text-xs md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Note modal shared by the Wins-of-the-Week panel */}
      {noteModalAthlete && (
        <CoachNoteModal
          athlete={noteModalAthlete}
          onClose={() => setNoteModalAthlete(null)}
        />
      )}
    </div>
  );
};

// ==================== COACHING MANAGER (Dark Theme) ====================
const CoachingManagerDark = () => {
  const [activeSubTab, setActiveSubTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ date: "", start_time: "", end_time: "" });
  const [approveModal, setApproveModal] = useState(null);
  const [zoomLink, setZoomLink] = useState("");
  const [coachNotes, setCoachNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showSubscribers, setShowSubscribers] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, availRes] = await Promise.all([
        fetch(`${API}/coach/requests`, { credentials: "include" }),
        fetch(`${API}/coach/availability`, { credentials: "include" })
      ]);
      if (reqRes.ok) setRequests((await reqRes.json()).requests || []);
      if (availRes.ok) setAvailability((await availRes.json()).slots || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = async () => {
    if (!newSlot.date || !newSlot.start_time || !newSlot.end_time) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      const response = await fetch(`${API}/coach/availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newSlot)
      });
      if (response.ok) {
        toast.success("Availability slot added!");
        setShowAddSlot(false);
        setNewSlot({ date: "", start_time: "", end_time: "" });
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to add slot");
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      const response = await fetch(`${API}/coach/availability/${slotId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Slot deleted!");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to delete slot");
    }
  };

  const handleApprove = async () => {
    if (!zoomLink) {
      toast.error("Please provide a Zoom link");
      return;
    }
    setProcessing(true);
    try {
      const response = await fetch(`${API}/coach/requests/${approveModal.request_id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ zoom_link: zoomLink, coach_notes: coachNotes })
      });
      if (response.ok) {
        toast.success("Session approved! Athlete notified.");
        setApproveModal(null);
        setZoomLink("");
        setCoachNotes("");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to approve");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (requestId) => {
    try {
      const response = await fetch(`${API}/coach/requests/${requestId}/reject`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Request rejected");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to reject");
    }
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-[#141B2D] rounded-2xl"></div>;
  }

  return (
    <div className="space-y-6">
      {/* Sub Tabs */}
      <div className="flex gap-2 bg-[#141B2D] border border-[#1E293B] rounded-xl p-1.5 w-fit">
        {["requests", "availability", "subscribers"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium capitalize transition-all ${
              activeSubTab === tab 
                ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25" 
                : "text-gray-400 hover:text-white"
            }`}
            data-testid={`coaching-subtab-${tab}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeSubTab === "subscribers" && <SubscribersManager />}

      {activeSubTab === "requests" && (
        <div className="bg-[#141B2D] border border-[#1E293B] rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Coaching Requests</h3>
          {requests.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No pending requests</p>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <div key={req.request_id} className="flex items-center justify-between p-4 bg-[#0B0F1A] border border-[#1E293B] rounded-xl hover:border-blue-500/30 transition-colors">
                  <div>
                    <p className="font-semibold text-white">{req.athlete_name || req.athlete_email}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(req.slot?.date).toLocaleDateString()} at {req.slot?.start_time}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setApproveModal(req)}
                      className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-500 text-white rounded-lg font-medium text-sm shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(req.request_id)}
                      className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg font-medium text-sm hover:bg-red-500/20 transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSubTab === "availability" && (
        <div className="bg-[#141B2D] border border-[#1E293B] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Your Availability</h3>
            <button
              onClick={() => setShowAddSlot(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-2 rounded-lg font-medium text-sm shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
            >
              <Plus className="w-4 h-4" />
              Add Slot
            </button>
          </div>

          {showAddSlot && (
            <div className="p-4 bg-[#0B0F1A] border border-[#1E293B] rounded-xl mb-4 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="date"
                  value={newSlot.date}
                  onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                  className="px-3 py-2 bg-[#141B2D] border border-[#1E293B] rounded-lg text-white focus:border-blue-500 outline-none"
                />
                <input
                  type="time"
                  value={newSlot.start_time}
                  onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                  className="px-3 py-2 bg-[#141B2D] border border-[#1E293B] rounded-lg text-white focus:border-blue-500 outline-none"
                />
                <input
                  type="time"
                  value={newSlot.end_time}
                  onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                  className="px-3 py-2 bg-[#141B2D] border border-[#1E293B] rounded-lg text-white focus:border-blue-500 outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleAddSlot} className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg text-sm font-medium">
                  Save
                </button>
                <button onClick={() => setShowAddSlot(false)} className="px-4 py-2 bg-[#1E293B] text-gray-300 rounded-lg text-sm font-medium hover:bg-[#2D3B4F] transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {availability.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No availability slots</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {availability.map((slot) => (
                <div key={slot.slot_id} className="flex items-center justify-between p-4 bg-[#0B0F1A] border border-[#1E293B] rounded-xl hover:border-blue-500/30 transition-colors">
                  <div>
                    <p className="font-medium text-white">{new Date(slot.date).toLocaleDateString()}</p>
                    <p className="text-sm text-gray-500">{slot.start_time} - {slot.end_time}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteSlot(slot.slot_id)}
                    className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approve Modal */}
      <AnimatePresence>
        {approveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setApproveModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[#141B2D] border border-[#1E293B] rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">Approve Session</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Zoom Link *</label>
                  <input
                    type="url"
                    value={zoomLink}
                    onChange={(e) => setZoomLink(e.target.value)}
                    placeholder="https://zoom.us/j/..."
                    className="w-full px-4 py-2 bg-[#0B0F1A] border border-[#1E293B] rounded-lg text-white focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Notes (optional)</label>
                  <textarea
                    value={coachNotes}
                    onChange={(e) => setCoachNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 bg-[#0B0F1A] border border-[#1E293B] rounded-lg text-white focus:border-blue-500 outline-none resize-none"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setApproveModal(null)}
                    className="flex-1 px-4 py-2 bg-[#1E293B] text-gray-300 rounded-lg font-medium hover:bg-[#2D3B4F] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-500 text-white rounded-lg font-medium disabled:opacity-50 shadow-lg shadow-emerald-500/25"
                  >
                    {processing ? "Sending..." : "Approve & Notify"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Keep the old CoachOverview for backwards compatibility
const CoachOverview = ({ stats }) => {
  return <CoachOverviewDark stats={stats} featuredCards={[]} onNavigate={() => {}} />;
};

export default CoachDashboard;
